import { useEffect, useState } from "react";
import { Star, BadgeCheck, Quote } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ProductPlaceholder } from "@/components/ProductPlaceholder";
import { getFeaturedReviews } from "@/lib/api";

const REVIEWS = [
  { name: "Giulia M.", city: "Milano", rating: 5, product: "iPhone 13 · 128 GB", text: "Onestamente ero titubante, il mio primo ricondizionato. Invece che sorpresa: sembra nuovo di zecca, nemmeno un graffio e la batteria tiene tutto il giorno. Rifarei l'acquisto a occhi chiusi." },
  { name: "Marco R.", city: "Torino", rating: 5, product: "Samsung Galaxy S22", text: "Pagato quasi 250€ in meno rispetto al negozio e non si vede mezzo segno. Arrivato il giorno dopo l'ordine. Che dire, contentissimo, si sente che c'è gente competente dietro." },
  { name: "Elena T.", city: "Bologna", rating: 4, product: "Sony WH-1000XM4", text: "Cuffie top. C'era un segnetto piccolo su un padiglione ma era scritto chiaro nella descrizione, quindi zero sorprese. Il suono e la cancellazione rumore sono quelli di sempre." },
  { name: "Davide P.", city: "Roma", rating: 5, product: "iPad Air", text: "Avevo un dubbio sulla condizione, ho scritto e mi hanno risposto in giornata con le foto vere del prodotto. Questa cosa mi ha convinto. iPad arrivato carico e pronto all'uso." },
  { name: "Sara L.", city: "Firenze", rating: 5, product: "iPhone 12", text: "Ho dato indietro il mio vecchio telefono e la valutazione finale era identica a quella del sito. Tutto liscio, nessuna fregatura dell'ultimo momento. Lo consiglio davvero." },
  { name: "Luca B.", city: "Napoli", rating: 5, product: "MacBook Air M1", text: "Non credevo che un ricondizionato potesse essere così. Cicli batteria bassissimi, velocissimo e silenzioso. Ho speso molto meno del listino: per me è stato un affare." },
  { name: "Chiara V.", city: "Verona", rating: 4, product: "AirPods Pro", text: "Sembrano nuovi davvero, la custodia ha giusto un mini segno come indicato. Mi piace che dicano le cose come stanno, senza girarci troppo intorno. Ci ricomprerò di sicuro." },
  { name: "Andrea F.", city: "Genova", rating: 5, product: "Galaxy Watch 5", text: "Spedizione super veloce e pacco curato nei dettagli. Alla fine il reso non mi è servito, ma sapere che c'era mi ha fatto ordinare tranquillo. Bell'esperienza, promossi." },
];

const FAQ = [
  { q: "Cosa significa ricondizionato?", a: "Un prodotto usato, verificato su 32 punti di controllo e rimesso a nuovo nel funzionamento, con garanzia di 12 mesi." },
  { q: "Che differenza c'è tra Come nuovo e Usato?", a: "Cambia solo l'estetica: Come nuovo non ha segni visibili, Usato ha lievi segni visibili in controluce. La funzionalità è identica." },
  { q: "Quanto dura la garanzia?", a: "Tutti i prodotti hanno 12 mesi di garanzia sui difetti di funzionamento." },
  { q: "Posso restituire il prodotto?", a: "Sì, hai 14 giorni per il reso gratuito se cambi idea." },
  { q: "Come valuto il mio dispositivo usato?", a: "Rispondi a poche domande nella pagina 'Valuta il tuo dispositivo' e ricevi una stima immediata." },
];

function ReviewCard({ r }) {
  return (
    <div className="mr-4 flex w-[300px] shrink-0 flex-col rounded-2xl bg-card p-5 sm:w-[360px]">
      <div className="mb-3 inline-flex items-center gap-1.5 self-start rounded-full bg-lime/15 px-2.5 py-1 text-[11px] font-semibold text-lime">
        <BadgeCheck className="h-3.5 w-3.5" strokeWidth={2} /> Acquisto verificato
      </div>
      <div className="flex gap-0.5">
        {Array.from({ length: 5 }).map((_, s) => (
          <Star key={s} className={`h-4 w-4 ${s < r.rating ? "fill-lime text-lime" : "text-tertiary"}`} strokeWidth={1.5} />
        ))}
      </div>
      <p className="mt-3 flex-1 text-[15px] leading-relaxed text-foreground line-clamp-6">{r.text}</p>
      <div className="mt-4 flex items-center gap-3 border-t border-white/5 pt-4">
        <ProductPlaceholder label={r.product} showLabel={false} className="h-10 w-10" />
        <div>
          <div className="text-sm font-bold text-foreground">{r.name} · <span className="font-medium text-muted-foreground">{r.city}</span></div>
          <div className="text-xs text-muted-foreground">{r.product}</div>
        </div>
      </div>
    </div>
  );
}

function Stars({ n }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, s) => (
        <Star key={s} className={`h-4 w-4 ${s < n ? "fill-lime text-lime" : "text-tertiary"}`} strokeWidth={1.5} />
      ))}
    </div>
  );
}

// Recensioni in evidenza con foto reali dei clienti — mostrata sotto la marquee.
function FeaturedReviewsPhotos() {
  const [list, setList] = useState(null);
  useEffect(() => {
    getFeaturedReviews()
      .then((r) => setList((r || []).filter((x) => (x.photos || []).length > 0)))
      .catch(() => setList([]));
  }, []);

  if (!list || list.length === 0) return null;
  const items = list.slice(0, 3);

  return (
    <div className="container-page mt-10" data-testid="home-reviews-featured-photos">
      <Reveal className="flex items-center gap-2">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-lime/15 text-lime">
          <Quote className="h-3.5 w-3.5" strokeWidth={2.5} />
        </span>
        <h3 className="font-display text-xl font-extrabold tracking-tight text-foreground sm:text-2xl">
          Recensioni in evidenza
        </h3>
      </Reveal>
      <p className="mt-1.5 text-[15px] text-muted-foreground">Le esperienze dei clienti, con le foto dei prodotti ricevuti.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((r) => {
          const photo = (r.photos || [])[0];
          return (
            <Reveal key={r.id} className="group overflow-hidden rounded-2xl bg-card ring-1 ring-white/5" data-testid={`featured-review-${r.id}`}>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={photo?.url}
                  alt={`Foto recensione di ${r.display_name}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                {(r.photos || []).length > 1 && (
                  <span className="absolute right-3 top-3 rounded-full bg-black/60 px-2 py-1 text-[11px] font-semibold text-chalk backdrop-blur-sm">
                    +{(r.photos || []).length - 1} foto
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 p-4">
                  <Stars n={r.stars} />
                  {r.verified && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-lime/90 px-2 py-0.5 text-[10px] font-bold text-ink">
                      <BadgeCheck className="h-3 w-3" strokeWidth={2.5} /> Verificato
                    </span>
                  )}
                </div>
              </div>
              <div className="p-5">
                {r.title && <p className="font-display text-base font-bold text-foreground">{r.title}</p>}
                <p className="mt-1.5 line-clamp-3 text-[14px] leading-relaxed text-muted-foreground">{r.text}</p>
                <div className="mt-4 border-t border-white/5 pt-3 text-xs text-muted-foreground">
                  <span className="font-bold text-foreground">{r.display_name}</span>
                  {r.city ? ` · ${r.city}` : ""}
                  {r.product_name ? <span className="mt-0.5 block text-tertiary">{r.product_name}</span> : null}
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  );
}

export function ReviewsMarquee({ title = "Cosa dicono i nostri clienti", subtitle = "Migliaia di ordini consegnati in tutta Italia." }) {
  const loop = [...REVIEWS, ...REVIEWS];
  return (
    <section className="section-tight" data-testid="home-reviews">
      <div className="container-page">
        <Reveal>
          <h2 className="display-lg font-extrabold text-foreground">{title}</h2>
          {subtitle && <p className="mt-2 text-[15px] text-muted-foreground">{subtitle}</p>}
        </Reveal>
      </div>
      {/* Scorrimento automatico continuo, in pausa al passaggio del mouse */}
      <div className="marquee mt-6 overflow-hidden">
        <div className="marquee-track flex w-max" style={{ "--marquee-duration": "60s" }}>
          {loop.map((r, i) => (
            <ReviewCard key={i} r={r} />
          ))}
        </div>
      </div>
      {/* Recensioni in evidenza con foto, subito sotto la marquee */}
      <FeaturedReviewsPhotos />
    </section>
  );
}

export function FaqSection({ title = "Le domande più frequenti" }) {
  return (
    <section className="section-tight" data-testid="home-faq">
      <div className="container-page max-w-3xl">
        <Reveal>
          <h2 className="display-lg font-extrabold text-foreground">{title}</h2>
        </Reveal>
        <Accordion type="single" collapsible className="mt-6">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
              <AccordionTrigger className="py-5 text-left text-base font-semibold hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function ReviewsFaq() {
  return (
    <>
      <ReviewsMarquee />
      <FaqSection />
    </>
  );
}
